import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit'
import { NumberScale } from 'legacy/lut/classes/models/partner360/NumberScale'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { map621ToConfigurationData } from 'legacy/lut/services/partner360/configuration/mappings/map621ToConfigurationData'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { getOfferingActualsValueTypeDefault } from 'partner360/helpers/getOfferingActualsValueTypeDefault'
import { getOfferingActualsValueTypes } from 'partner360/helpers/offerings/programOfferings'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { changeFinancialYear } from './api/changeFinancialYear'
import { changeProgramType } from './api/changeProgramType'
import { initialState } from './configs'
import { PARTNER360_CONFIGURATION_NAMESPACE } from './constants'
import { I621Form } from './models/I621Form'
import { ISalesTypeItem } from './models/iSalesTypeItem'
import { IProgramType } from './models/programType'
import { IPartner360ConfigurationState } from './models/state'
import {
  partnerDataFulfilled,
  partnerDataPending,
  partnerDataRejected,
} from './partnerDataLifecycle'

const applyPartnerData = (state: Draft<IPartner360ConfigurationState>, payload: I621Form) => {
  state.data = map621ToConfigurationData(payload, state.programType)
  const selectedValue =
    state.data.currencyData.localCurrency || state.data.currencyData.defaultCurrency

  if (selectedValue) {
    state.currency = selectedValue
  }
  state.actualsType = state.data.actualsData.valueTypeDefault
  state.saleType =
    state.data.salesTypes.find((it) => it.isDefault) || state.data.salesTypes[0] || null
}

type TUserSelections = Pick<IPartner360ConfigurationState, 'currency' | 'actualsType'> & {
  saleType?: string
}

/* Puts back what the user picked over the defaults the partner data has just set; a sale type
   the new data no longer offers stays at the default. */
const restoreUserSelections = (
  state: Draft<IPartner360ConfigurationState>,
  { currency, actualsType, saleType }: TUserSelections
) => {
  state.currency = currency
  state.actualsType = actualsType
  state.saleType = state.data.salesTypes.find((it) => it.salesType === saleType) ?? state.saleType
}

const configurationSlice = createSlice({
  name: PARTNER360_CONFIGURATION_NAMESPACE,
  initialState,
  reducers: {
    changeCurrency: (state, action: PayloadAction<string>) => {
      state.currency = action.payload
    },
    changeActualsType: (state, action: PayloadAction<ActualsValueTypes>) => {
      state.actualsType = action.payload
    },
    changeSaleType: (state, action: PayloadAction<ISalesTypeItem>) => {
      state.saleType = action.payload
    },
    updateProgramType: (state, action: PayloadAction<IProgramType>) => {
      const index = state.data.programTypes.findIndex(
        (it) => it.programType === action.payload.programType
      )

      if (index !== -1) {
        state.data.programTypes[index] = action.payload
      }
    },
    updateOutOfScope: (state, action: PayloadAction<boolean>) => {
      state.data.outOfScope = action.payload
    },
    changeOfferingsMode: (state, action: PayloadAction<boolean>) => {
      state.isNewOfferings = action.payload
    },
    changeOutOfScope: (state, action: PayloadAction<boolean>) => {
      state.data.outOfScope = action.payload
    },
    changeMoneyScale: (state, action: PayloadAction<NumberScale>) => {
      state.moneyScale = action.payload
    },
    resetProgramType: (state) => {
      state.programType = ProgramTypes.None
    },
    setAvailableActualsTypes: (state, action: PayloadAction<ActualsValueTypes[]>) => {
      state.availableActualsTypes = action.payload
    },
    setAvailableCurrencies: (state, action: PayloadAction<string[]>) => {
      state.availableCurrencies = action.payload
    },
    changeContractualPlanning: (state, action: PayloadAction<boolean>) => {
      state.data.planning.isCorePrintContractualPlanningActive = action.payload
    },
    changeBpsCategoryIsActive: (state, action: PayloadAction<boolean>) => {
      state.data.planning.isBpsCategoryActive = action.payload
    },
    reviewKeyInitiative: (state) => {
      state.data.keyInitiativesPending = state.data.keyInitiativesPending.map((el) => {
        if (el.Offering === state.programType) {
          return { ...el, KIPendingCount: el.KIPendingCount - 1 }
        }
        return el
      })
    },
  },
  extraReducers(builder) {
    builder
      .addCase(changeProgramType.fulfilled, (state, action) => {
        const previousDefault = state.data.actualsData.valueTypeDefault
        const offering = state.data.programTypesExtended.find(
          (item) => item.Offering === action.payload
        )
        const defaultType = getOfferingActualsValueTypeDefault(offering)
        state.programType = action.payload
        state.data.actualsData.valueTypes = getOfferingActualsValueTypes(
          state.data.programOfferingsCatalog,
          action.payload
        )
        state.data.actualsData.valueTypeDefault = defaultType
        if (defaultType && (!state.actualsType || state.actualsType === previousDefault)) {
          state.actualsType = defaultType
        }
      })
      .addCase(changeFinancialYear.fulfilled, (state, action) => {
        state.data.fYear = action.payload
      })

      .addCase(partnerDataPending, (state) => {
        state.status = RequestStatus.PENDING
        state.message = 'fetching partner360 configuration...'
      })
      .addCase(partnerDataRejected, (state, action) => {
        state.status = RequestStatus.REJECTED
        if (action.payload) {
          state.message = action.payload
        }
      })
      .addCase(partnerDataFulfilled, (state, action) => {
        const selections: TUserSelections = {
          currency: state.currency,
          actualsType: state.actualsType,
          saleType: state.saleType?.salesType,
        }

        state.status = RequestStatus.FULFILLED
        state.message = 'configuration fetched successful'
        applyPartnerData(state, action.payload)

        if (action.meta.keepSelections) {
          restoreUserSelections(state, selections)
        }
      })
  },
})

export const {
  updateProgramType,
  changeOfferingsMode,
  changeMoneyScale,
  resetProgramType,
  setAvailableActualsTypes,
  setAvailableCurrencies,
  changeOutOfScope,
  changeContractualPlanning,
  changeBpsCategoryIsActive,
  reviewKeyInitiative,
  changeSaleType,
  changeActualsType,
  changeCurrency,
} = configurationSlice.actions

export default configurationSlice.reducer
